import {
  CreateTaskInventory,
  CreateTaskWaypoint,
  Customer,
  EmulatedDeliveryEvents,
  EmulatedDeliveryScheduleEvent,
  Merchant,
  PickupDropoffOption,
  Task,
  TaskTypeId,
  Team,
} from '@bringg/types';

import { TAX_FEE } from 'Consts';
import { DeliveryOptions, ECommerceSettings } from 'Types';

export const buildBaseTask = (team: Team, customer: Customer): Task => {
  const nowTimestamp = new Date().getTime();

  // @ts-ignore
  return {
    ready_to_execute: true,
    test: true,
    title: `Order ${nowTimestamp}`,
    external_id: `${nowTimestamp}-${Math.floor(Math.random() * 1000)}`,
    customer: customer,
    teams: [team.id],
    payment_method: 1,
    left_to_be_paid: 0,
  } as Task;
};

export const buildDeliveryTask = (
  settings: ECommerceSettings,
  customer: Customer,
  taskInventories: CreateTaskInventory[],
  delivery: DeliveryOptions,
  merchant: Merchant
): Task => {
  const team = settings.team;
  const task = buildBaseTask(team, { ...customer, phone: undefined });

  const dropOffPWayPoint: CreateTaskWaypoint = {
    address: customer.address,
    address_type: 1,
    merchant_id: merchant.id,
    customer: customer,
    inventory: taskInventories,
    phone: customer.phone,
    scheduled_at: delivery.scheduled_at,
    no_earlier_than: delivery.dropOff.no_earlier_than,
    no_later_than: delivery.dropOff.no_later_than,
    pickup_dropoff_option: PickupDropoffOption.DROPOFF,
    position: 2,
  };

  const pickUpWayPoint: CreateTaskWaypoint = {
    address: team.address,
    customer_id: team.default_customer_id,
    inventory: taskInventories,
    merchant_id: merchant.id,
    position: 1,
    no_earlier_than: delivery.pickUp.no_earlier_than,
    no_later_than: delivery.pickUp.no_later_than,
    pickup_dropoff_option: PickupDropoffOption.PICKUP,
  };

  const inventoriesPrice = taskInventories
    .map((task) => (task.original_quantity as number) * (task.price as number))
    .reduce((prev, curr) => prev + curr, 0);
  const totalPrice = inventoriesPrice + delivery.price;

  const delivery_emulating_settings: EmulatedDeliveryScheduleEvent[] = [
    {
      name: EmulatedDeliveryEvents.AssignDriverAndStart,
      interval: settings.assignDriverIntervalSeconds * 1000,
    },
    {
      name: EmulatedDeliveryEvents.CheckInPickUp,
      interval: settings.checkInPickupIntervalSeconds * 1000,
    },
    {
      name: EmulatedDeliveryEvents.CheckOutPickUp,
      interval: settings.checkOutPickupIntervalSeconds * 1000,
    },
    {
      name: EmulatedDeliveryEvents.CheckInDropOff,
      interval: settings.checkInDropOffIntervalSeconds * 1000,
    },
    {
      name: EmulatedDeliveryEvents.CheckOutDropOff,
      interval: settings.checkOutDropOffIntervalSeconds * 1000,
    },
    {
      // TODO: check why CheckOutDropOff and FinishOrder have the same outcome and remove or add field for FinishOrder
      name: EmulatedDeliveryEvents.FinishOrder,
      interval: settings.finishOrder * 1000,
    },
  ].filter((e) => e.interval);

  return {
    ...task,
    additional_attributes: {
      ...task.additional_attributes,
      delivery_emulating_settings,
    },
    tag_id: settings.tag.id,
    way_points: [pickUpWayPoint, dropOffPWayPoint],
    task_type_id: TaskTypeId.DELIVERY,
    total_price: totalPrice,
    delivery_price: delivery.price,
    delivery_cost: delivery.price,
    price_before_tax: totalPrice * (1 - TAX_FEE),
    tax_price: totalPrice * TAX_FEE,
  } as Task;
};
